import { memoize } from 'lodash';

const rawContainsBit = memoize(
  (field: number, bits: number): boolean => {
    const limit = Math.pow(2, 32);

    if (field >= limit || bits >= limit) {
      const bin = field.toString(2).split('').reverse().join('');
      const binBits = bits.toString(2);
      return bin.length >= binBits.length && bin?.[binBits.length - 1] === '1';
    }

    return (field & bits) === bits;
  },
  (field: number, bits: number) => `${field}-${bits}`,
);

export const containsBit = (field: any, bits: number): boolean => {
  if (!field) {
    return false;
  }
  field = typeof field === 'number' ? field : field.Flags || field.flags;

  return rawContainsBit(field, bits);
};

export namespace Field {
  export enum Flag {
    required = 2 ** 0,
    radio = 2 ** 1,
    checkBox = 2 ** 2,
    includeInSearches = 2 ** 3,
    includeInCoding = 2 ** 4,
    includeInExport = 2 ** 5, // OBSOLETE. Starting from now we will export everything that was included in grid.
    includeInWorkflow = 2 ** 6,
    dropDown = 2 ** 7,
    systemField = 2 ** 8,
    textField = 2 ** 9,
    textAreaField = 2 ** 10,
    numberField = 2 ** 11,
    dateField = 2 ** 12,
    readOnly = 2 ** 13,
    includeInGrid = 2 ** 14,
    includeInRegistration = 2 ** 15,
    includeInNotification = 2 ** 16,
    includeInMassCoding = 2 ** 17,
    virtualField = 2 ** 18,
    includeInContact = 2 ** 19,
    booleanField = 2 ** 20,
    multipleInt = 2 ** 21,
    includeInSuggestions = 2 ** 22,
    includeInFacets = 2 ** 24,
    applyTimeZone = 2 ** 25,
    useTimePart = 2 ** 26, // previously it was 2 ** 25
    DefaultVirtual = 2 ** 27,
    hidden = 2 ** 28,
    includeInAdvancedSearch = 2 ** 29,
    CustomOption3 = 2 ** 30,
    richText = 2 ** 32,
    showInlineDescription = 2 ** 33,
    topic = 2 ** 34,
    user = 2 ** 35,

    // for custom ui validation we can add value after 2^30
    email = 2 ** 31, // custom ui validation for room-creation(used only for UI)
  }

  export class Is {
    public static required = (flagsOrField) => containsBit(flagsOrField, Field.Flag.required);
    public static radio = (flagsOrField) => containsBit(flagsOrField, Field.Flag.radio);
    public static checkBox = (flagsOrField) => containsBit(flagsOrField, Field.Flag.checkBox);
    public static includeInSearches = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInSearches);
    public static includeInFacets = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInFacets);
    public static includeInAdvancedSearch = (flagsOrField) =>
      containsBit(flagsOrField, Field.Flag.includeInAdvancedSearch);
    public static includeInCoding = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInCoding);
    // public static includeInExport = flagsOrField => containsBit(flagsOrField, Field.Flag.includeInExport); // OBSOLETE. Starting from now we will export everything that was included in grid.
    public static includeInWorkflow = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInWorkflow);
    public static dropDown = (flagsOrField) => containsBit(flagsOrField, Field.Flag.dropDown);
    public static system = (flagsOrField) => containsBit(flagsOrField, Field.Flag.systemField);
    public static text = (flagsOrField) => containsBit(flagsOrField, Field.Flag.textField);
    public static textArea = (flagsOrField) => containsBit(flagsOrField, Field.Flag.textAreaField);
    public static number = (flagsOrField) => containsBit(flagsOrField, Field.Flag.numberField);
    public static date = (flagsOrField) => containsBit(flagsOrField, Field.Flag.dateField);
    public static readOnly = (flagsOrField) => containsBit(flagsOrField, Field.Flag.readOnly);
    public static includeInGrid = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInGrid);
    public static includeInRegistration = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInRegistration);
    public static includeInNotification = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInNotification);
    public static includeInMassCoding = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInMassCoding);
    public static virtual = (flagsOrField) => containsBit(flagsOrField, Field.Flag.virtualField);
    public static includeInContact = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInContact);
    public static boolean = (flagsOrField) => containsBit(flagsOrField, Field.Flag.booleanField);
    public static includeInSuggestions = (flagsOrField) => containsBit(flagsOrField, Field.Flag.includeInSuggestions);
    public static requiredEmail = (flagsOrField) => containsBit(flagsOrField, Field.Flag.email);
    public static applyTimeZone = (flagsOrField) => containsBit(flagsOrField, Field.Flag.applyTimeZone);
    public static useTimePart = (flagsOrField) => containsBit(flagsOrField, Field.Flag.useTimePart);
    public static hidden = (flagsOrField) => containsBit(flagsOrField, Field.Flag.hidden);
    public static ssuSpecific = (flagsOrField) => containsBit(flagsOrField, Field.Flag.CustomOption3);
    public static richText = (flagsOrField) => containsBit(flagsOrField, Field.Flag.richText);
    public static defaultVirtual = (flagsOrField) => containsBit(flagsOrField, Field.Flag.DefaultVirtual);
    public static showInlineDescription = (flagsOrField) => containsBit(flagsOrField, Field.Flag.showInlineDescription);
    public static user = (flagsOrField) => containsBit(flagsOrField, Field.Flag.user);
    public static topic = (flagsOrField) => containsBit(flagsOrField, Field.Flag.topic);
    public static multipleInt = (flagsOrField) => containsBit(flagsOrField, Field.Flag.multipleInt);
  }

  export function setFlagOn(field: any, flag: Flag) {
    const value = field.Flags || field.flags;
    if (flag >= Math.pow(2, 32) || value >= Math.pow(2, 32)) {
      let bitValue = value.toString(2);
      let bitFlag = flag.toString(2);
      bitValue = bitValue.padStart(bitFlag.length, '0');

      const newBitArray = bitValue.split('').reverse();
      newBitArray[bitFlag.length - 1] = '1';
      const newBitValue = parseInt(newBitArray.reverse().join(''), 2);

      if (typeof field.Flags === 'number') {
        field.Flags = newBitValue;
      }

      if (typeof field.flags === 'number') {
        field.flags = newBitValue;
      }
      return;
    }

    if (typeof field.Flags === 'number') {
      field.Flags |= flag;
    }

    if (typeof field.flags === 'number') {
      field.flags |= flag;
    }
  }

  export function setFlagOff(field: any, flag: Flag) {
    const value = field.Flags || field.flags;
    if (flag >= Math.pow(2, 32) || value >= Math.pow(2, 32)) {
      let bitValue = value.toString(2);
      let bitFlag = flag.toString(2);
      bitValue = bitValue.padStart(bitFlag.length, '0');

      const newBitArray = bitValue.split('').reverse();
      newBitArray[bitFlag.length - 1] = '0';
      const newBitValue = parseInt(newBitArray.reverse().join(''), 2);

      if (typeof field.Flags === 'number') {
        field.Flags = newBitValue;
      }

      if (typeof field.flags === 'number') {
        field.flags = newBitValue;
      }
      return;
    }

    if (typeof field.Flags === 'number') {
      field.Flags &= ~flag;
    }

    if (typeof field.flags === 'number') {
      field.flags &= ~flag;
    }
  }
}
